import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../services/api';
import { startTracking } from '../services/tracking';
import { StorytellingIntro, StorytellingPromise, StorytellingDSC } from '../components/storytelling';
import { StorytellingFlow } from '../components/storytelling';
import QuotacaoAereoView from '../components/aereo/QuotacaoAereoView';

/**
 * ClientView — Experiência do cliente ao abrir o link da cotação.
 * 
 * Se a cotação tem múltiplas opções (pai + filhos publicados),
 * mostra abas flutuantes no topo para o cliente alternar.
 * 
 * Fluxo: intro → promise → DSC → [opção tabs] → storytelling flow → resume
 */

// ─── Option Tabs Component ───
function OptionTabs({ options, activeIndex, onChange }) {
  if (!options || options.length <= 1) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      zIndex: 100,
      backgroundColor: 'rgba(9, 7, 119, 0.95)',
      backdropFilter: 'blur(12px)',
      borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
      padding: '0.5rem 1rem',
      display: 'flex',
      justifyContent: 'center',
      gap: '0.5rem'
    }}>
      {options.map((opt, i) => {
        const isActive = i === activeIndex;
        const price = (opt.final_price || 0).toLocaleString('pt-BR', {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        });

        return (
          <button
            key={opt.id || i}
            onClick={() => onChange(i)}
            style={{
              padding: '0.5rem 1rem',
              borderRadius: '100px',
              border: isActive
                ? '2px solid #5DD4C3'
                : '1px solid rgba(255, 255, 255, 0.2)',
              backgroundColor: isActive
                ? 'rgba(93, 212, 195, 0.15)'
                : 'transparent',
              color: isActive ? '#5DD4C3' : 'rgba(255, 255, 255, 0.7)',
              fontSize: '0.8125rem',
              fontWeight: isActive ? 600 : 400,
              cursor: 'pointer',
              transition: 'all 0.2s',
              whiteSpace: 'nowrap',
              fontFamily: "'Inter', sans-serif"
            }}
          >
            <span>{opt.option_name || `Opção ${i + 1}`}</span>
            <span style={{
              marginLeft: '0.4rem',
              fontSize: '0.75rem',
              opacity: 0.8
            }}>
              R$ {price}
            </span>
          </button>
        );
      })}
    </div>
  );
}

export default function ClientView() {
  const { link: publicLink } = useParams();
  const [quote, setQuote] = useState(null);
  const [options, setOptions] = useState([]);
  const [activeOptionIndex, setActiveOptionIndex] = useState(0);
  const [narratives, setNarratives] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentStep, setCurrentStep] = useState('intro');

  // ─── Engagement Tracking ───
  const cleanupRef = useRef(null);

  useEffect(() => {
    if (quote?.id) {
      cleanupRef.current = startTracking(quote.id);
    }
    return () => {
      if (cleanupRef.current) {
        cleanupRef.current();
        cleanupRef.current = null;
      }
    };
  }, [quote?.id]);

  useEffect(() => {
    loadQuote();
  }, [publicLink]);

  const loadQuote = async () => {
    try {
      const data = await api.viewQuote(publicLink);
      setQuote(data);

      // Opções: se backend retorna options[], usa; senão, single option
      const opts = data.options && data.options.length > 0
        ? data.options
        : [data];
      setOptions(opts);

      // Carrega narrativas apenas para pacotes — aéreo não usa storytelling
      if (data.quote_type !== 'aereo') {
        try {
          const storytellingData = await api.getStorytelling(data.id);
          setNarratives(storytellingData.narratives || {});
        } catch (narrativeErr) {
          console.warn('Narrativas não disponíveis, usando padrão');
        }
      }

      const visitedKey = `visited_quote_${data.id}`;
      const hasVisited = localStorage.getItem(visitedKey);

      if (data.is_returning_client || hasVisited) {
        setCurrentStep('flow');
      } else {
        setCurrentStep('intro');
      }

      setLoading(false);
    } catch (err) {
      setError('Cotação não encontrada');
      setLoading(false);
    }
  };

  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href);
    alert('Link copiado!');
  };

  const handleFlowComplete = () => {
    if (quote) {
      localStorage.setItem(`visited_quote_${quote.id}`, 'true');
    }
  };

  const handleOptionChange = useCallback((index) => {
    setActiveOptionIndex(index);
    // Reset para o flow (pula intro já vista)
    setCurrentStep('flow');
  }, []);

  // Quote ativa baseada na opção selecionada
  const activeQuote = options[activeOptionIndex] || quote;
  const hasMultipleOptions = options.length > 1;
  
  // Padding top para compensar o header de tabs
  const topPadding = hasMultipleOptions ? '48px' : '0px';

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#090777',
        fontFamily: "'Inter', sans-serif",
      }}>
        <p style={{ color: 'rgba(255, 255, 255, 0.7)' }}>Carregando...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#090777',
        fontFamily: "'Inter', sans-serif",
      }}>
        <p style={{ color: 'rgba(255, 255, 255, 0.7)' }}>{error}</p>
      </div>
    );
  }

  // ── Cotação aérea — entrega direta, sem storytelling ─────────────────────
  if (quote?.quote_type === 'aereo') {
    return (
      <QuotacaoAereoView
        quote={quote}
        options={options}
      />
    );
  }
  // ─────────────────────────────────────────────────────────────────────────

  // Wrapper que adiciona tabs + top padding
  const withTabs = (content) => (
    <div style={{ paddingTop: topPadding }}>
      <OptionTabs
        options={options}
        activeIndex={activeOptionIndex}
        onChange={handleOptionChange}
      />
      {content}
    </div>
  );

  if (currentStep === 'intro') {
    return withTabs(
      <StorytellingIntro
        quote={activeQuote}
        onContinue={() => setCurrentStep('promise')}
        onShare={handleShare}
      />
    );
  }

  if (currentStep === 'promise') {
    return withTabs(
      <StorytellingPromise
        quote={activeQuote}
        narratives={narratives}
        onContinue={() => setCurrentStep('dsc')}
        onBack={() => setCurrentStep('intro')}
        onShare={handleShare}
      />
    );
  }

  if (currentStep === 'dsc') {
    return withTabs(
      <StorytellingDSC
        onContinue={() => {
          if (quote) {
            localStorage.setItem(`visited_quote_${quote.id}`, 'true');
          }
          setCurrentStep('flow');
        }}
        onBack={() => setCurrentStep('promise')}
        onShare={handleShare}
      />
    );
  }

  return withTabs(
    <StorytellingFlow
      key={activeQuote?.id || activeOptionIndex}
      quote={activeQuote}
      narratives={narratives}
      onComplete={handleFlowComplete}
      onShare={handleShare}
      whatsappNumber="5577988229531"
    />
  );
}
